<script setup>
import axios from "axios";
import {GuestAppApiUrl, HttpStatusLocked} from "@/constants";
import {useGuestAuthStore} from "@/stores/guestAuth";
import {useToast} from "vue-toastification";
import {useBookingStore} from "@/stores/booking";
import {useI18n} from "vue-i18n";
import {useApartmentStore} from "@/stores/apartment";
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import Modal from "@/components/Modal.vue";
import AppIcon from "@/components/AppIcon.vue";
import {lockedUntil} from "@/misc/doorLocks";

const guestAuth = useGuestAuthStore()
const bookingStore = useBookingStore()
const apartmentStore = useApartmentStore()
const i18n = useI18n()
const toast = useToast()

const isLoading = ref({front_door: false, apartment_door: false})
const lastCommand = ref({front_door: null, apartment_door: null})
const apartmentDoorConfirmModal = ref(false)

// A ticking clock, so every time-based gate below actually re-evaluates while
// the guest is standing in front of the door.
const now = ref(Date.now())
let clock = null

const doorTypes = {
  frontDoor: 'front_door',
  apartmentDoor: 'apartment_door',
}
const doorActions = {
  unlock: 'unlock',
  lock: 'lock',
}
const waitTimeout = 3000 // 3s
const feedbackTimeout = 4500 // 4.5s
const reqLockTimeOut = 20000 // 20s

const intervals = []

const isCheckedIn = computed(() => bookingStore.isCheckedIn)
const isAvailable = computed(() => now.value > bookingStore.checkInDateTime.toMillis?.())
const location = computed(() => apartmentStore.apartment.location ?? {})

const isReqLocked = computed(() => ({
  [doorTypes.frontDoor]: now.value < lockedUntil[doorTypes.frontDoor],
  [doorTypes.apartmentDoor]: now.value < lockedUntil[doorTypes.apartmentDoor],
}))

const frontDoorBusy = computed(() => isLoading.value[doorTypes.frontDoor])
const frontDoorDisabled = computed(
    () => !isAvailable.value || frontDoorBusy.value || isReqLocked.value[doorTypes.frontDoor]
)
const apartmentDoorDisabled = computed(
    () => !isAvailable.value || isLoading.value[doorTypes.apartmentDoor] || isReqLocked.value[doorTypes.apartmentDoor]
)

onMounted(() => {
  clock = setInterval(() => now.value = Date.now(), 1000)
})

onBeforeUnmount(() => {
  clearInterval(clock)
  intervals.forEach(clearInterval)
})

// One token per door: a second command must be able to cancel the first
// command's reset without the first one clearing the second one's confirmation.
const flashTokens = {front_door: 0, apartment_door: 0}

function flash(type, action) {
  const token = ++flashTokens[type]
  lastCommand.value[type] = action

  setTimeout(() => {
    if (flashTokens[type] === token) {
      lastCommand.value[type] = null
    }
  }, feedbackTimeout)
}

function btnDoorLockedTimeout(type) {
  let countdown = reqLockTimeOut / 1000
  lockedUntil[type] = Date.now() + reqLockTimeOut

  const tid = toast.warning(i18n.t('alerts.doorRequestLocked', {count: countdown}), {timeout: reqLockTimeOut})

  const ival = setInterval(() => {
    countdown--
    toast.update(tid, {content: i18n.t('alerts.doorRequestLocked', {count: countdown})})
  }, 1000)
  intervals.push(ival)

  setTimeout(() => {
    toast.dismiss(tid)
    clearInterval(ival)
  }, reqLockTimeOut)
}

function doorAction(action, type) {
  if (isLoading.value[type]) {
    return;
  }

  isLoading.value[type] = true;

  axios.post(
      '/v1/door/action',
      {action, type},
      {
        baseURL: GuestAppApiUrl,
        headers: {
          "X-API-Key": guestAuth.token,
        }
      }
  )
      .then(() => {
        let msg = action === doorActions.unlock ? i18n.t('doorOpened') : i18n.t('doorClosed')

        // Deliberately not cleared on unmount: the lock was already driven, so
        // the guest must get the confirmation even after switching tabs.
        setTimeout(() => {
          toast.success(msg)
          isLoading.value[type] = false
          flash(type, action)
        }, waitTimeout)
      })
      .catch(err => {
        if (err.response && err.response.status === HttpStatusLocked) {
          btnDoorLockedTimeout(type)
        } else {
          console.error(err)
          toast.error(i18n.t('alerts.somethingWentWrong'))
        }

        isLoading.value[type] = false
      })
}

function openApartmentDoor() {
  apartmentDoorConfirmModal.value = false
  doorAction(doorActions.unlock, doorTypes.apartmentDoor)
}
</script>

<template>
  <div class="ww-screen">
    <h1 class="ww-title">{{ $t('doorKeys') }}</h1>
    <p class="ww-subtitle">{{ $t('keys.intro') }}</p>

    <div v-if="!isCheckedIn" class="ww-card ww-card--dashed keys-locked">
      <app-icon name="lock" :size="32" class="keys-locked__icon"/>
      <div class="keys-locked__title">{{ $t('keys.blocked') }}</div>
      <p class="keys-locked__text">{{ $t('keys.blockedSub') }}</p>
      <router-link :to="{name: 'booking.checkin'}" class="ww-btn ww-btn--primary ww-btn--sm keys-locked__btn">
        {{ $t('home.startCheckin') }}
      </router-link>
    </div>

    <template v-else>
      <p v-if="!isAvailable" class="keys-hint">
        <app-icon name="schedule" :size="18"/>
        {{ $t('keys.notAvailableYet', {date: bookingStore.summary.checkIn}) }}
      </p>

      <div class="keys">
        <section v-if="apartmentStore.apartment.smartLocks?.frontDoor" class="ww-card ww-card--navy front-door">
          <div class="front-door__head">
            <span class="ww-plate ww-plate--onNavy">
              <app-icon name="apartment" :size="22"/>
            </span>
            <div>
              <h2 class="front-door__title">{{ $t('frontDoor') }}</h2>
              <p class="front-door__hint">{{ $t('keys.frontHint') }}</p>
            </div>
          </div>

          <div class="front-door__action">
            <button
                type="button"
                class="front-door__btn"
                :class="{
                  'front-door__btn--sent': lastCommand[doorTypes.frontDoor],
                  'front-door__btn--disabled': frontDoorDisabled,
                }"
                :disabled="frontDoorDisabled"
                :aria-label="$t('open')"
                @click="doorAction(doorActions.unlock, doorTypes.frontDoor)"
            >
              <span v-if="frontDoorBusy" class="ww-spinner front-door__spinner"></span>
              <app-icon v-else :name="lastCommand[doorTypes.frontDoor] ? 'check' : 'key'" :size="40"/>
            </button>
            <div class="front-door__caption">
              {{ lastCommand[doorTypes.frontDoor] ? $t('keys.cmdSent') : $t('keys.tapToOpen') }}
            </div>
          </div>
        </section>

        <section class="ww-card apartment-door">
          <div class="apartment-door__head">
            <span class="ww-plate">
              <app-icon name="door_front" :size="22"/>
            </span>
            <div class="apartment-door__identity">
              <h2 class="apartment-door__title">{{ $t('apartmentDoor') }}</h2>
              <p
                  class="apartment-door__status"
                  :class="{'apartment-door__status--sent': lastCommand[doorTypes.apartmentDoor]}"
              >
                {{ lastCommand[doorTypes.apartmentDoor] ? $t('keys.cmdSent') : $t('keys.statusUnknown') }}
              </p>
            </div>
            <span v-if="location.floor !== undefined" class="ww-pill">
              {{ location.floor }}. {{ $t('floor') }}, {{ $t(location.side) }}
            </span>
          </div>

          <div class="apartment-door__actions">
            <button
                type="button"
                class="door-cmd"
                :class="{'door-cmd--active': lastCommand[doorTypes.apartmentDoor] === doorActions.unlock}"
                :disabled="apartmentDoorDisabled"
                @click="apartmentDoorConfirmModal = true"
            >
              <span v-if="isLoading[doorTypes.apartmentDoor]" class="ww-spinner door-cmd__spinner"></span>
              <app-icon v-else name="lock_open_right" :size="27"/>
              {{ $t('open') }}
            </button>
            <button
                type="button"
                class="door-cmd"
                :class="{'door-cmd--active': lastCommand[doorTypes.apartmentDoor] === doorActions.lock}"
                :disabled="apartmentDoorDisabled"
                @click="doorAction(doorActions.lock, doorTypes.apartmentDoor)"
            >
              <span v-if="isLoading[doorTypes.apartmentDoor]" class="ww-spinner door-cmd__spinner"></span>
              <app-icon v-else name="lock" :size="27"/>
              {{ $t('close') }}
            </button>
          </div>
        </section>

        <p class="keys-footnote">
          <app-icon name="cloud" :size="18"/>
          {{ $t('keys.footnote') }}
        </p>
      </div>
    </template>

    <!-- Kept inside this element on purpose: a second root node would turn the
         routed component into a Fragment, which the shell's <transition
         mode="out-in"> cannot finish leaving. The modal teleports to body
         anyway, so nesting changes nothing visually. -->
    <modal v-model:show="apartmentDoorConfirmModal" :title="$t('apartmentDoorConfirmModal.title')">
      <template #content>
        <p v-html="$t('apartmentDoorConfirmModal.content')"></p>
      </template>
      <template #footer>
        <button type="button" class="ww-btn ww-btn--primary ww-btn--sm" @click="openApartmentDoor()">
          <app-icon name="lock_open_right" :size="20"/>
          {{ $t('open') }}
        </button>
      </template>
    </modal>
  </div>
</template>

<style scoped>
.keys-locked {
  margin-top: 20px;
}

.keys-locked__icon {
  color: var(--ww-icon-muted);
  margin: 0 auto;
}

.keys-locked__title {
  margin-top: 12px;
  font-size: 17px;
  font-weight: 700;
  color: var(--ww-text);
}

.keys-locked__text {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--ww-text-muted);
}

.keys-locked__btn {
  margin-top: 16px;
}

.keys-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 13px;
  background: var(--ww-warn-tint);
  border: 1px solid var(--ww-warn-border);
  border-radius: var(--ww-r-sm);
  font-size: 13px;
  font-weight: 500;
  color: var(--ww-warn-ink);
}

.keys {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
}

/* Front door -------------------------------------------------------------- */

.front-door {
  border-radius: var(--ww-r-2xl);
  padding: 22px 22px 26px;
}

.front-door__head {
  display: flex;
  align-items: center;
  gap: 13px;
}

.front-door__title {
  font-size: 18px;
  font-weight: 700;
}

.front-door__hint {
  margin-top: 4px;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--ww-on-navy-dim);
}

.front-door__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  margin-top: 24px;
}

.front-door__btn {
  display: grid;
  place-items: center;
  width: 112px;
  height: 112px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--ww-primary);
  box-shadow: 0 0 0 9px rgba(18, 144, 194, 0.15);
  color: #fff;
  transition: background-color 0.25s ease, transform 0.15s ease;
}

.front-door__btn:active:not(:disabled) {
  transform: scale(0.96);
}

.front-door__btn--sent {
  background: var(--ww-success);
  box-shadow: none;
  animation: ww-pulse 1.9s ease-out infinite;
}

.front-door__btn--disabled {
  background: rgba(255, 255, 255, 0.12);
  box-shadow: none;
  color: rgba(255, 255, 255, 0.45);
}

.front-door__spinner {
  width: 34px;
  height: 34px;
  border-width: 3px;
}

.front-door__caption {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #b6c7d7;
}

/* Apartment door ---------------------------------------------------------- */

.apartment-door {
  border-radius: var(--ww-r-2xl);
  padding: 22px 22px 24px;
}

.apartment-door__head {
  display: flex;
  align-items: center;
  gap: 13px;
}

/* .ww-pill is nowrap by default, which makes its min-content the full label and
   squeezes the title off the card on narrow phones. Here it may wrap instead. */
.apartment-door__head .ww-pill {
  flex: 0 1 auto;
  min-width: 0;
  white-space: normal;
  text-align: center;
}

.apartment-door__identity {
  flex: 1 1 auto;
  min-width: 0;
}

.apartment-door__title {
  font-size: 18px;
  font-weight: 700;
  color: var(--ww-text);
}

.apartment-door__status {
  margin-top: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ww-text-soft);
}

.apartment-door__status--sent {
  color: var(--ww-success);
}

.apartment-door__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 20px;
}

.door-cmd {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  padding: 18px 10px;
  border: 1px solid var(--ww-border-input);
  border-radius: var(--ww-r-md);
  background: var(--ww-surface);
  color: var(--ww-navy-soft);
  font-size: 15px;
  font-weight: 700;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.door-cmd:hover:not(:disabled) {
  border-color: var(--ww-primary-border);
  background: var(--ww-surface-hover);
}

.door-cmd--active {
  border-color: var(--ww-success);
  background: var(--ww-success-tint);
  color: var(--ww-success-ink);
}

.door-cmd:disabled {
  background: var(--ww-disabled-bg);
  border-color: transparent;
  color: var(--ww-text-disabled);
}

.door-cmd__spinner {
  width: 27px;
  height: 27px;
  border-width: 3px;
}

.keys-footnote {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 6px;
  padding: 0 4px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--ww-text-soft);
}

.keys-footnote .ww-icon {
  color: #a9b5c1;
}
</style>
